import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  mobilityVehicles as configuredVehicles,
  routeById,
  type MobilityVehicle as ConfiguredVehicle,
  type VehicleStatus,
} from "../../data/mobility";
import { nearestRouteProgress, positionAlongRoute, type LngLat } from "../../lib/geo";
import { supabaseClient } from "../../lib/supabase";

export interface VehicleSnapshot {
  vehicleId: string;
  routeId: string;
  coordinate: LngLat;
  bearing: number;
  speedKph: number;
  progress: number;
  distanceRemainingKm: number;
  etaMinutes: number;
  status: VehicleStatus;
  nextStopName: string;
  nextStopMinutes: number;
  updatedAt: string;
  source: "gps";
  segmentIndex: number;
}

interface MobilityContextValue {
  vehicles: readonly ConfiguredVehicle[];
  snapshots: Record<string, VehicleSnapshot>;
}

const MobilityContext = createContext<MobilityContextValue | null>(null);

function snapshotFromGps(
  vehicle: ConfiguredVehicle,
  row: Record<string, unknown>,
): VehicleSnapshot | null {
  const coordinate: LngLat = [Number(row.longitude), Number(row.latitude)];
  if (!coordinate.every(Number.isFinite)) return null;

  const route = routeById(vehicle.routeId);
  const projected = nearestRouteProgress(route.coordinates, coordinate);
  const position = positionAlongRoute(route.coordinates, projected.progress);
  const speedKph = Math.max(0, Number(row.speed_kph) || 0);
  const remaining = Math.max(0, position.totalKm - position.travelledKm);
  const nextStop =
    route.stops.find((stop) => stop.progress > projected.progress) ??
    route.stops.at(-1)!;
  const effectiveSpeed = Math.max(speedKph, 8);

  return {
    vehicleId: vehicle.id,
    routeId: vehicle.routeId,
    coordinate,
    bearing: Number(row.bearing) || 0,
    speedKph,
    progress: projected.progress,
    distanceRemainingKm: remaining,
    etaMinutes: Math.ceil((remaining / effectiveSpeed) * 60),
    status: speedKph < 1 ? "DETENIDO" : "EN RUTA",
    nextStopName: nextStop.name,
    nextStopMinutes: Math.max(
      0,
      Math.ceil(
        (((nextStop.progress - projected.progress) * position.totalKm) /
          effectiveSpeed) *
          60,
      ),
    ),
    updatedAt: String(row.recorded_at),
    source: "gps",
    segmentIndex: position.segmentIndex,
  };
}

export function MobilityProvider({ children }: { children: ReactNode }) {
  const [snapshots, setSnapshots] = useState<Record<string, VehicleSnapshot>>({});

  useEffect(() => {
    const client = supabaseClient;
    if (!client) return;

    let active = true;
    const idMap = new Map<string, ConfiguredVehicle>();
    const channel = client.channel("public-mobility-positions");

    const applyPosition = (row: Record<string, unknown>) => {
      if (!active) return;
      const vehicle = idMap.get(String(row.vehicle_id));
      if (!vehicle) return;
      const snapshot = snapshotFromGps(vehicle, row);
      if (!snapshot) return;
      setSnapshots((current) => ({ ...current, [vehicle.id]: snapshot }));
    };

    void client
      .from("vehicles")
      .select("id, code")
      .in(
        "code",
        configuredVehicles.map((vehicle) => vehicle.trackingCode),
      )
      .then(({ data, error }) => {
        if (!active || error) return;
        data?.forEach((row) => {
          const vehicle = configuredVehicles.find(
            (candidate) => candidate.trackingCode === row.code,
          );
          if (vehicle) idMap.set(row.id, vehicle);
        });

        const vehicleIds = [...idMap.keys()];
        if (vehicleIds.length > 0) {
          void client
            .from("vehicle_positions")
            .select("vehicle_id, latitude, longitude, speed_kph, bearing, recorded_at")
            .in("vehicle_id", vehicleIds)
            .order("recorded_at", { ascending: false })
            .limit(200)
            .then(({ data: positions }) => {
              const applied = new Set<string>();
              positions?.forEach((position) => {
                const vehicleId = String(position.vehicle_id);
                if (applied.has(vehicleId)) return;
                applied.add(vehicleId);
                applyPosition(position);
              });
            });
        }

        channel
          .on(
            "postgres_changes",
            { event: "INSERT", schema: "public", table: "vehicle_positions" },
            (payload) => applyPosition(payload.new as Record<string, unknown>),
          )
          .subscribe();
      });

    return () => {
      active = false;
      void client.removeChannel(channel);
    };
  }, []);

  const value = useMemo<MobilityContextValue>(
    () => ({
      vehicles: configuredVehicles.filter((vehicle) => Boolean(snapshots[vehicle.id])),
      snapshots,
    }),
    [snapshots],
  );

  return <MobilityContext.Provider value={value}>{children}</MobilityContext.Provider>;
}

export function useMobility() {
  const context = useContext(MobilityContext);
  if (!context) throw new Error("useMobility debe usarse dentro de MobilityProvider");
  return context;
}
